import {analyticsConsentGranted} from './consent';

const ALLOWED_EVENTS = new Set([
  'pricing_viewed',
  'plan_selected',
  'checkout_started',
  'checkout_completed',
  'checkout_abandoned',
  'expansion_started',
  'upgrade_prompt_impression',
  'upgrade_prompt_clicked',
  'upgrade_prompt_dismissed',
  'upgrade_gate_reached',
  'plans_viewed',
  'checkout_cancelled',
  'subscription_activation_pending',
  'subscription_activated',
  'upgrade_return_completed',
  'start_auth_method_selected',
  'start_account_creation_started',
  'start_account_created',
  'start_auth_cancelled',
  'start_existing_user_signin_selected',
  'marketing_prompt_shown',
  'marketing_consent_granted',
  'background_voice_started',
  'background_voice_stopped',
  'background_voice_cancelled',
  'background_voice_processing_succeeded',
  'background_voice_processing_failed',
  'background_voice_result_applied',
  'background_voice_result_discarded',
]);

const PLAN_KEY_PATTERN = /^(company|expansion)_(monthly|annual)_(eur|usd)$/;
const CAMPAIGN_KEY_PATTERN = /^(utm_source|utm_medium|utm_campaign|utm_term|utm_content)$/;
const SAFE_ENUMS = Object.freeze({
  reason: new Set([
    'free_status',
    'scenario_map_remaining',
    'scenario_map_limit',
    'ai_followup_remaining',
    'ai_followup_limit',
    'participant_limit',
    'organizer_limit',
    'checkout_cancelled',
    'checkout_processing',
  ]),
  surface: new Set([
    'free_status_strip',
    'dashboard_new_scenario',
    'scenario_creation',
    'scenario_continuation',
    'participant_invitation',
    'workspace_team',
    'settings_plan',
    'checkout_return',
    'start_account_creation',
    'start_first_map',
  ]),
  authenticationMethod: new Set(['google', 'email']),
  flow: new Set(['start', 'authenticated_create']),
  durationBucket: new Set(['up_to_15s', 'up_to_45s', 'up_to_90s']),
  errorCategory: new Set([
    'permission_denied',
    'microphone_unavailable',
    'request_timed_out',
    'network_failure',
    'background_voice_rate_limited',
    'background_voice_request_in_progress',
    'background_voice_request_already_used',
    'background_voice_no_speech',
    'audio_empty',
    'audio_too_long',
    'audio_too_large',
    'audio_type_unsupported',
    'audio_signature_invalid',
    'audio_signature_mismatch',
    'audio_duration_unreadable',
    'audio_upload_invalid',
    'audio_upload_timed_out',
    'background_voice_session_invalid',
    'background_voice_temporarily_unavailable',
    'background_voice_processing_failed',
    'background_voice_length_unresolved',
  ]),
  usageState: new Set(['unused', 'available', 'one_remaining', 'exhausted']),
  remainingCategory: new Set(['unused', 'available', 'one_remaining', 'exhausted']),
  returnAction: new Set(['dashboard', 'scenario_create', 'ai_followup', 'participants', 'organizers']),
});
const emittedOnce = new Set();

function safeProperties(properties = {}) {
  const result = {};
  if (PLAN_KEY_PATTERN.test(properties.planKey || '')) result.planKey = properties.planKey;
  if (['month', 'year'].includes(properties.interval)) result.interval = properties.interval;
  if (['eur', 'usd'].includes(properties.currency)) result.currency = properties.currency;
  Object.entries(SAFE_ENUMS).forEach(([key, values]) => {
    if (values.has(properties[key])) result[key] = properties[key];
  });
  if (typeof properties.billingAuthority === 'boolean') {
    result.billingAuthority = properties.billingAuthority;
  }
  if (typeof properties.condensationRequired === 'boolean') {
    result.condensationRequired = properties.condensationRequired;
  }
  Object.entries(properties.campaign || {}).forEach(([key, value]) => {
    const trimmed = typeof value === 'string' ? value.trim().slice(0, 120) : '';
    if (CAMPAIGN_KEY_PATTERN.test(key) && trimmed) {
      if (!result.campaign) result.campaign = {};
      result.campaign[key] = trimmed;
    }
  });
  return result;
}

export function emitProductAnalyticsOnce(eventName, dedupeKey, properties = {}) {
  const key = `${eventName}:${String(dedupeKey || '').slice(0, 240)}`;
  if (emittedOnce.has(key)) return false;
  const emitted = emitProductAnalytics(eventName, properties);
  if (emitted) emittedOnce.add(key);
  return emitted;
}

export function emitProductAnalytics(eventName, properties = {}) {
  if (
    !ALLOWED_EVENTS.has(eventName) ||
    typeof window === 'undefined' ||
    !analyticsConsentGranted()
  ) return false;
  const detail = {event: eventName, ...safeProperties(properties)};
  window.dispatchEvent(new CustomEvent('affinityai:analytics', {detail}));
  if (Array.isArray(window.dataLayer)) window.dataLayer.push(detail);
  return true;
}
